import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';

const GEOLOCATION_URL = 'https://ipapi.co/country/';
const SESSION_KEY = 'valley-visitor-language';

// Countries and territories where Spanish is an official, national or primary language.
export const SPANISH_SPEAKING_COUNTRIES = new Set([
  'AR', 'BO', 'CL', 'CO', 'CR', 'CU', 'DO', 'EC', 'ES', 'GQ',
  'GT', 'HN', 'MX', 'NI', 'PA', 'PE', 'PR', 'PY', 'SV', 'UY', 'VE'
]);

export const getLanguageForCountry = (countryCode) => (
  SPANISH_SPEAKING_COUNTRIES.has(String(countryCode || '').trim().toUpperCase()) ? 'es' : 'en'
);

export const getBrowserLanguage = (languages = []) => {
  const languageList = Array.isArray(languages) ? languages : [languages];
  return languageList.some((language) => String(language || '').toLowerCase().startsWith('es'))
    ? 'es'
    : 'en';
};

export async function detectVisitorLanguage({ fetcher, languages, signal } = {}) {
  const request = fetcher || (typeof window !== 'undefined' ? window.fetch : undefined);
  const browserLanguages = languages || (
    typeof navigator !== 'undefined' ? navigator.languages || [navigator.language] : []
  );

  if (typeof request === 'function') {
    try {
      const response = await request(GEOLOCATION_URL, {
        cache: 'no-store',
        headers: { Accept: 'text/plain' },
        signal
      });
      if (response.ok) {
        const countryCode = (await response.text()).trim().toUpperCase();
        if (/^[A-Z]{2}$/.test(countryCode)) return getLanguageForCountry(countryCode);
      }
    } catch (error) {
      if (error?.name === 'AbortError') throw error;
      // Browser language is the intentional fallback when IP lookup is unavailable.
    }
  }

  return getBrowserLanguage(browserLanguages);
}

const LanguageContext = createContext({
  language: 'en',
  isSpanish: false,
  t: (english) => english
});

export function LanguageProvider({ children }) {
  const cachedLanguage = (() => {
    try {
      const value = window.sessionStorage.getItem(SESSION_KEY);
      return value === 'es' || value === 'en' ? value : null;
    } catch {
      return null;
    }
  })();

  const [language, setLanguage] = useState(
    cachedLanguage || getBrowserLanguage(
      typeof navigator !== 'undefined' ? navigator.languages || [navigator.language] : []
    )
  );

  useEffect(() => {
    if (cachedLanguage) return undefined;

    const controller = new AbortController();
    const timeoutId = window.setTimeout(() => controller.abort(), 4000);

    detectVisitorLanguage({ signal: controller.signal })
      .then((detectedLanguage) => {
        setLanguage(detectedLanguage);
        try {
          window.sessionStorage.setItem(SESSION_KEY, detectedLanguage);
        } catch {
          // Language detection still works when storage is disabled.
        }
      })
      .catch(() => {
        // The initial browser-language fallback remains active after a timeout.
      })
      .finally(() => window.clearTimeout(timeoutId));

    return () => {
      window.clearTimeout(timeoutId);
      controller.abort();
    };
  }, [cachedLanguage]);

  useEffect(() => {
    document.documentElement.lang = language;
  }, [language]);

  const t = useCallback(
    (english, spanish) => (language === 'es' ? spanish : english),
    [language]
  );

  const value = useMemo(() => ({
    language,
    isSpanish: language === 'es',
    t
  }), [language, t]);

  return <LanguageContext.Provider value={value}>{children}</LanguageContext.Provider>;
}

export const useLanguage = () => useContext(LanguageContext);
